import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { PageHeader, Panel, Spinner } from "../components/Panels.js";

interface CompanyContext {
  id: string;
  version: number;
  content_md: string;
  tone_section: string | null;
  is_active: number;
  updated_by_staff_id: string | null;
  created_at: string;
  updated_at: string;
}

interface CompanyContextResponse {
  ok: true;
  active: CompanyContext | null;
  versions: Array<{
    id: string;
    version: number;
    updated_by_staff_id: string | null;
    created_at: string;
  }>;
}

const SECTION_HINTS = [
  "## How I think about problems",
  "## What good looks like",
  "## Red flags I want flagged",
  "## Tone and style for digests",
];

export function SettingsPage() {
  const [data, setData] = useState<CompanyContextResponse | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [tone, setTone] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    api
      .get<CompanyContextResponse>("/settings/company-context")
      .then((r) => {
        setData(r);
        setDraft(r.active?.content_md ?? "");
        setTone(r.active?.tone_section ?? "");
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(refresh, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company-context", {
        content_md: draft,
        tone_section: tone || null,
      });
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="p-6 text-sm text-sentiment-overwhelmed">{error}</div>;
  if (!data) return <div className="p-6"><Spinner /></div>;

  return (
    <>
      <PageHeader
        title="Operating principles"
        subtitle="Shape how Compass interprets check-ins, sets thresholds for blockers, and writes the daily digest. Each save bumps the version; the active version is used on the next AI call."
      />
      <div className="px-6 grid grid-cols-3 gap-4">
        <Panel
          title={`v${data.active?.version ?? 1} · active`}
          action={
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs px-3 py-1 rounded-md bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save new version"}
            </button>
          }
          className="col-span-2"
        >
          <div className="text-xs text-ink-dimmer mb-2">
            Suggested sections:{" "}
            {SECTION_HINTS.map((h, i) => (
              <span key={h} className="font-mono">
                {i > 0 ? " · " : ""}
                {h.replace("## ", "")}
              </span>
            ))}
          </div>
          <textarea
            className="w-full bg-bg-base border border-bg-line2 rounded p-3 font-mono text-sm text-ink min-h-[400px]"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={SECTION_HINTS.join("\n\n")}
          />
          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink-dimmer">
              Tone-and-style override (used by the digest model)
            </label>
            <textarea
              className="mt-2 w-full bg-bg-base border border-bg-line2 rounded p-3 font-mono text-sm text-ink min-h-[100px]"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              placeholder="Optional. Leave blank to fall back to the relevant section in the document above."
            />
          </div>
        </Panel>

        <Panel title="Version history">
          <ul className="space-y-2">
            {data.versions.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="font-mono text-ink">v{v.version}</span>
                <span className="text-xs text-ink-dim">
                  {v.created_at.slice(0, 16).replace("T", " ")}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
